/**
 * New-job form model + pure logic (extracted from NewJob.tsx so it is unit-
 * testable without rendering the component). Constraints mirror `validate_form`
 * in rm-ui `types.rs` and the `job-spec` contract (RM-116). Validation
 * messages are resolved via i18next keys (see `validateForm`).
 */
import type { TFunction } from 'i18next'
import type { InterpolationMethod, UpscalingPresetSpec } from '../lib/schemas'

/** A per-step interpolation choice; `''` means "leave the chain default" (unset). */
export type InterpolationChoice = InterpolationMethod | ''

export interface SelectedFile {
  name: string
  size: number
}

export type JobKind =
  | 'blender_render'
  | 'empty'
  | 'upscaling'
  | 'turntable_frame_bake'

/** Upscaler options (mirror job_spec::UpscalerKind, PascalCase worker tokens). */
export type UpscalerKind = 'Topaz' | 'Simple' | 'ClearReality'
/**
 * Investment-overlay choice. `none` disables the merge; `Source` (native mode only)
 * uses the raw source frames for the building (no upscaler); otherwise an upscaler.
 */
export type InvestmentUpscaler = 'none' | 'Source' | UpscalerKind

/**
 * Target-size mode. `width` = an explicit target width (px). `widest` = native mode:
 * the worker takes the target from the widest available source and re-details it with
 * Topaz (only Topaz makes sense — Simple/ClearReality would be a no-op at native width).
 */
export type TargetMode = 'width' | 'widest'

/** Topaz upscale factor (mirror job_spec::TopazUpscaleFactor). */
export type TopazUpscaleFactor = 2 | 4 | 6
export const TOPAZ_UPSCALE_FACTORS: TopazUpscaleFactor[] = [2, 4, 6]
/** Topaz model strength (mirror job_spec::TopazPower, PascalCase worker tokens). */
export type TopazPower = 'Low' | 'Medium' | 'High'
export const TOPAZ_POWERS: TopazPower[] = ['Low', 'Medium', 'High']

export interface RenderFormData {
  jobKind: JobKind
  name: string
  sceneFile: SelectedFile | null
  engine: string
  width: number
  height: number
  frameStart: number
  frameEnd: number
  outputFormat: string
  samples: number
  /** Empty-job wait duration (string while editing; parsed/validated on submit). */
  waitSecs: string
  /** Empty-job process exit code (0 = success; non-zero drives the job to failed). */
  exitCode: string
  // ── Upscaling job (RM-117) ──────────────────────────────────────────────
  /** Creator Online view id `<uuid>-<scene>-<render>`. */
  viewId: string
  /** Name of the selected preset while the form still matches it; `undefined`
   * for a custom job or one edited away from its preset. Sent only to tag the
   * resulting render, never affects upscaling params. */
  presetName?: string
  mainUpscaler: UpscalerKind
  /** Optional exact Topaz application version; blank accepts any installed version. */
  topazVersion: string
  /** Topaz upscale factor (used only when main=Topaz). */
  topazUpscaleFactor: TopazUpscaleFactor
  /** Topaz model strength (used only when main=Topaz). */
  topazPower: TopazPower
  /** Fixed sRGB highlight adjustment applied immediately before every Topaz pass. */
  maxBrightnessAdjustment: boolean
  /** `width` = explicit `targetSize`; `widest` = native mode (target from widest source). */
  targetMode: TargetMode
  targetSize: number
  investmentUpscaler: InvestmentUpscaler
  /** Investment Topaz factor (used only when investment=Topaz). */
  investmentUpscaleFactor: TopazUpscaleFactor
  /** Investment Topaz strength (used only when investment=Topaz). */
  investmentPower: TopazPower
  /** Optional overlay mask feather in px; blank → worker default. */
  mergeFeatherPx: string
  /** Optional Creator Online frame-elements set number (`-f-N`) for the overlay
   * mask; blank → the worker resolves the view's production set, else the highest.
   * Only sent when an investment overlay is active. */
  frameElementsId: string
  // Per-branch chaiNNer resize interpolation (experimental); '' → chain default.
  // Only the field matching the chosen upscaler for that branch is sent.
  mainSimpleInterpolation: InterpolationChoice
  mainDownscaleInterpolation: InterpolationChoice
  mainResizeInterpolation: InterpolationChoice
  investmentSimpleInterpolation: InterpolationChoice
  investmentDownscaleInterpolation: InterpolationChoice
  investmentResizeInterpolation: InterpolationChoice
  colorViewId: string
  depthViewId: string
  outputTargetId: string
  deliveryApiKey: string
  outputFrameCount: string
  manifestFile: SelectedFile | null
  manifest: unknown | null
}

export const DEFAULT_FORM: RenderFormData = {
  jobKind: 'blender_render',
  name: '',
  sceneFile: null,
  engine: 'cycles',
  width: 1920,
  height: 1080,
  frameStart: 1,
  frameEnd: 1,
  outputFormat: 'png',
  samples: 80,
  waitSecs: '5',
  exitCode: '0',
  viewId: '',
  mainUpscaler: 'Topaz',
  topazVersion: '',
  topazUpscaleFactor: 4,
  topazPower: 'Medium',
  maxBrightnessAdjustment: false,
  targetMode: 'width',
  targetSize: 3840,
  investmentUpscaler: 'none',
  investmentUpscaleFactor: 4,
  investmentPower: 'Medium',
  mergeFeatherPx: '',
  frameElementsId: '',
  mainSimpleInterpolation: '',
  mainDownscaleInterpolation: '',
  mainResizeInterpolation: '',
  investmentSimpleInterpolation: '',
  investmentDownscaleInterpolation: '',
  investmentResizeInterpolation: '',
  colorViewId: '',
  depthViewId: '',
  outputTargetId: '',
  deliveryApiKey: '',
  outputFrameCount: '1200',
  manifestFile: null,
  manifest: null,
}

/** Max scene archive size (mirror server presign guard): 10 GB. */
export const MAX_SCENE_BYTES = 10 * 1024 * 1024 * 1024
/** Empty-job wait bounds — single source of truth is job_spec::WaitSecs (1..=300). */
const WAIT_SECS_MIN = 1
const WAIT_SECS_MAX = 300
/** Exit code is a u8 (mirror job_spec::EmptySpec::exit_code). */
const EXIT_CODE_MIN = 0
const EXIT_CODE_MAX = 255

/**
 * Client-side validation. Returns a map of field → localized error message
 * (resolved via `t` from the field's `entity.*.field.*.error.*` key); empty map
 * means valid. Empty jobs validate only wait/exit-code; Blender jobs validate
 * scene file + render params.
 */
export function validateForm(
  form: RenderFormData,
  t: TFunction,
): Record<string, string> {
  const errors: Record<string, string> = {}

  // Empty job: only wait duration + exit code matter (ranges mirror job-spec).
  if (form.jobKind === 'empty') {
    const secs = Number.parseInt(form.waitSecs.trim(), 10)
    if (
      !Number.isInteger(secs) ||
      secs < WAIT_SECS_MIN ||
      secs > WAIT_SECS_MAX
    ) {
      errors.wait_secs = t('entity.empty.field.waitSecs.error.range')
    }
    const code = Number.parseInt(form.exitCode.trim(), 10)
    if (
      !Number.isInteger(code) ||
      code < EXIT_CODE_MIN ||
      code > EXIT_CODE_MAX
    ) {
      errors.exit_code = t('entity.empty.field.exitCode.error.range')
    }
    return errors
  }

  // Upscaling job: view id + target size. Topaz factor/power are fixed-choice
  // selects (2/4/6 · Low/Medium/High), so they can't be out of range — no
  // validation beyond what the UI already constrains.
  if (form.jobKind === 'upscaling') {
    if (form.viewId.trim() === '') {
      errors.view_id = t('entity.upscaling.field.viewId.error.required')
    }
    // Native mode (targetMode='widest') derives the width from the source, so a
    // target width is only required in explicit-width mode.
    if (form.targetMode === 'width' && form.targetSize < 1) {
      errors.target_size = t('entity.upscaling.field.targetSize.error.min')
    }
    return errors
  }

  if (form.jobKind === 'turntable_frame_bake') {
    if (form.colorViewId.trim() === '') errors.color_view_id = 'Required'
    if (form.depthViewId.trim() === '') errors.depth_view_id = 'Required'
    if (
      form.colorViewId.trim() !== '' &&
      form.colorViewId.trim() === form.depthViewId.trim()
    ) {
      errors.depth_view_id = 'Color and depth view IDs must differ'
    }
    if (form.outputTargetId.trim() === '') errors.output_target_id = 'Required'
    if (form.deliveryApiKey.trim() === '') errors.delivery_api_key = 'Required'
    const count = Number.parseInt(form.outputFrameCount.trim(), 10)
    if (!Number.isInteger(count) || count < 1 || count > 2400) {
      errors.output_frame_count = 'Must be between 1 and 2400'
    }
    if (form.manifest === null) errors.manifest = 'Select a valid JSON manifest'
    return errors
  }

  if (form.sceneFile === null) {
    errors.scene_file = t('entity.blenderRender.field.scene.error.required')
  }
  if (form.sceneFile !== null) {
    if (form.sceneFile.size > MAX_SCENE_BYTES) {
      const sizeGb = form.sceneFile.size / (1024 * 1024 * 1024)
      errors.scene_file = t('entity.blenderRender.field.scene.error.tooLarge', {
        size: sizeGb.toFixed(1),
      })
    }
  }
  if (form.width === 0 || form.width > 7680) {
    errors.width = t('entity.blenderRender.field.width.error.range')
  }
  if (form.height === 0 || form.height > 4320) {
    errors.height = t('entity.blenderRender.field.height.error.range')
  }
  if (form.frameStart < 1) {
    errors.frame_start = t('entity.blenderRender.field.frameStart.error.min')
  }
  if (form.frameEnd < form.frameStart) {
    errors.frame_end = t('entity.blenderRender.field.frameEnd.error.min')
  }
  if (
    form.frameEnd >= form.frameStart &&
    form.frameEnd - form.frameStart + 1 > 10000
  ) {
    errors.frame_end = t('entity.blenderRender.field.frameEnd.error.max')
  }
  if (form.samples === 0 || form.samples > 10000) {
    errors.samples = t('entity.blenderRender.field.samples.error.range')
  }

  return errors
}

/** Build the typed `job_spec` submit request (mirror submit_request_from_form).
 * Empty → `{ type: 'empty', wait_secs, exit_code }`; Blender → animation `mode_params`. */
export function submitRequestFromForm(form: RenderFormData): unknown {
  const name = form.name.length > 0 ? form.name : undefined

  if (form.jobKind === 'empty') {
    // validateForm has already gated these into range; fall back defensively.
    const secs = Number.parseInt(form.waitSecs.trim(), 10)
    const wait_secs = Number.isInteger(secs) ? secs : 1
    const code = Number.parseInt(form.exitCode.trim(), 10)
    const exit_code = Number.isInteger(code) ? code : 0
    // Empty jobs have no scene file to name themselves after, so default the
    // name to their wait duration + exit code when the user leaves it blank.
    const emptyName =
      form.name.length > 0
        ? form.name
        : `Empty ${wait_secs}s · exit ${exit_code}`
    return {
      job_spec: { type: 'empty', wait_secs, exit_code },
      name: emptyName,
    }
  }

  if (form.jobKind === 'upscaling') {
    // Upscaling jobs have no scene file to name themselves after, so default the
    // name to the view id when the user leaves it blank.
    const upscalingName =
      form.name.length > 0 ? form.name : form.viewId.trim() || undefined
    const spec: Record<string, unknown> = {
      type: 'upscaling',
      view_id: form.viewId.trim(),
      ...upscalingSpecFromForm(form),
    }
    // Carry the preset name (if a clean preset is selected) to tag the render.
    if (form.presetName) spec.preset_name = form.presetName
    // Per-job frame-elements set (which building silhouette to mask with). Only
    // meaningful with an investment overlay; sent verbatim for the worker to
    // resolve (a nonexistent number falls back to prod/highest server-side).
    if (form.investmentUpscaler !== 'none') {
      const feId = Number.parseInt(form.frameElementsId.trim(), 10)
      if (Number.isInteger(feId) && feId > 0) spec.frame_elements_id = feId
    }
    return { job_spec: spec, name: upscalingName }
  }

  if (form.jobKind === 'turntable_frame_bake') {
    return {
      color_view_id: form.colorViewId.trim(),
      depth_view_id: form.depthViewId.trim(),
      output_target_id: form.outputTargetId.trim(),
      delivery_api_key: form.deliveryApiKey,
      output_frame_count: Number.parseInt(form.outputFrameCount.trim(), 10),
      manifest: form.manifest,
      name,
    }
  }

  return {
    job_spec: {
      type: 'blender_render',
      mode_params: [
        {
          mode: 'animation',
          engine: form.engine,
          width: form.width,
          height: form.height,
          frame_start: form.frameStart,
          frame_end: form.frameEnd,
          output_format: form.outputFormat,
          samples: form.samples,
        },
      ],
    },
    name,
  }
}

/**
 * Switch the target-size mode, coercing dependent fields so the form stays valid.
 * Native mode (`widest`) only supports a Topaz main and a none/Topaz/Source overlay
 * (Simple/ClearReality would be a no-op at native width); explicit mode has no
 * `Source` overlay. Returns the patched form.
 */
export function applyTargetMode(
  form: RenderFormData,
  mode: TargetMode,
): RenderFormData {
  if (mode === 'widest') {
    return {
      ...form,
      targetMode: 'widest',
      mainUpscaler: 'Topaz', // only Topaz re-details at native width
      // Native overlay allows none | Topaz | Source; drop Simple/ClearReality.
      investmentUpscaler:
        form.investmentUpscaler === 'Topaz' ||
        form.investmentUpscaler === 'Source'
          ? form.investmentUpscaler
          : 'none',
    }
  }
  return {
    ...form,
    targetMode: 'width',
    // 'Source' only exists in native mode — fall back to no overlay.
    investmentUpscaler:
      form.investmentUpscaler === 'Source' ? 'none' : form.investmentUpscaler,
  }
}

// ── Upscaling presets ⇄ form ───────────────────────────────────────────────

/**
 * The upscaling config a preset captures — the form's upscaling fields minus the
 * per-view `view_id`. Shared by job-submit (spread into the job
 * spec) and preset-save. Overlay params (feather included) only apply when an
 * overlay upscaler is chosen, mirroring the form where they're revealed together.
 */
export function upscalingSpecFromForm(
  form: RenderFormData,
): UpscalingPresetSpec {
  const spec: UpscalingPresetSpec = {
    main_upscaler: form.mainUpscaler,
  }
  // Explicit width → send target_size; native mode (widest) omits it entirely.
  if (form.targetMode === 'width') {
    spec.target_size = form.targetSize
  }
  if (form.mainUpscaler === 'Topaz') {
    spec.topaz_upscale_factor = form.topazUpscaleFactor
    spec.topaz_power = form.topazPower
  }
  const usesTopaz =
    form.mainUpscaler === 'Topaz' || form.investmentUpscaler === 'Topaz'
  if (usesTopaz && form.maxBrightnessAdjustment) {
    spec.max_brightness_adjustment = true
  }
  if (usesTopaz && form.topazVersion.trim() !== '') {
    spec.topaz_version = form.topazVersion.trim()
  }
  if (form.investmentUpscaler !== 'none') {
    if (form.investmentUpscaler === 'Source') {
      // Native-mode source overlay: raw building frames, no upscaler.
      spec.investment_use_source = true
    } else {
      spec.investment_upscaler = form.investmentUpscaler
      if (form.investmentUpscaler === 'Topaz') {
        spec.investment_upscale_factor = form.investmentUpscaleFactor
        spec.investment_power = form.investmentPower
      }
    }
    const feather = Number.parseInt(form.mergeFeatherPx.trim(), 10)
    if (Number.isInteger(feather)) {
      spec.merge_feather_px = feather
    }
  }
  // Per-branch interpolation (experimental) — only the field for the chosen
  // upscaler of each branch is sent, and only when explicitly picked.
  if (form.mainUpscaler === 'Simple' && form.mainSimpleInterpolation !== '')
    spec.main_simple_interpolation = form.mainSimpleInterpolation
  if (form.mainUpscaler === 'Topaz') {
    if (form.mainDownscaleInterpolation !== '')
      spec.main_downscale_interpolation = form.mainDownscaleInterpolation
    if (form.mainResizeInterpolation !== '')
      spec.main_resize_interpolation = form.mainResizeInterpolation
  }
  if (
    form.investmentUpscaler === 'Simple' &&
    form.investmentSimpleInterpolation !== ''
  )
    spec.investment_simple_interpolation = form.investmentSimpleInterpolation
  if (form.investmentUpscaler === 'Topaz') {
    if (form.investmentDownscaleInterpolation !== '')
      spec.investment_downscale_interpolation =
        form.investmentDownscaleInterpolation
    if (form.investmentResizeInterpolation !== '')
      spec.investment_resize_interpolation = form.investmentResizeInterpolation
  }
  return spec
}

/**
 * Whether two upscaling specs are equivalent (all fields, treating an absent
 * optional as `null`). Used to detect whether the form has drifted from the
 * selected preset so the UI can offer "override" vs. nothing. Kept field-wise
 * (not `JSON.stringify`) because key order/optional presence differs between the
 * form builder and the server payload.
 */
export function upscalingSpecEqual(
  a: UpscalingPresetSpec,
  b: UpscalingPresetSpec,
): boolean {
  return (
    a.main_upscaler === b.main_upscaler &&
    (a.topaz_version ?? null) === (b.topaz_version ?? null) &&
    (a.target_size ?? null) === (b.target_size ?? null) &&
    (a.investment_use_source ?? false) === (b.investment_use_source ?? false) &&
    (a.topaz_upscale_factor ?? null) === (b.topaz_upscale_factor ?? null) &&
    (a.topaz_power ?? null) === (b.topaz_power ?? null) &&
    (a.max_brightness_adjustment ?? false) ===
      (b.max_brightness_adjustment ?? false) &&
    (a.investment_upscaler ?? null) === (b.investment_upscaler ?? null) &&
    (a.investment_upscale_factor ?? null) ===
      (b.investment_upscale_factor ?? null) &&
    (a.investment_power ?? null) === (b.investment_power ?? null) &&
    (a.merge_feather_px ?? null) === (b.merge_feather_px ?? null) &&
    (a.main_simple_interpolation ?? null) ===
      (b.main_simple_interpolation ?? null) &&
    (a.main_downscale_interpolation ?? null) ===
      (b.main_downscale_interpolation ?? null) &&
    (a.main_resize_interpolation ?? null) ===
      (b.main_resize_interpolation ?? null) &&
    (a.investment_simple_interpolation ?? null) ===
      (b.investment_simple_interpolation ?? null) &&
    (a.investment_downscale_interpolation ?? null) ===
      (b.investment_downscale_interpolation ?? null) &&
    (a.investment_resize_interpolation ?? null) ===
      (b.investment_resize_interpolation ?? null)
  )
}

/**
 * Apply a preset's config onto the form: returns the partial form patch to merge.
 * Fields absent from the preset fall back to sensible form defaults (overlay off,
 * empty feather) rather than leaving stale values from a previously-picked preset.
 */
export function applyPresetToForm(
  spec: UpscalingPresetSpec,
): Partial<RenderFormData> {
  const patch: Partial<RenderFormData> = {
    mainUpscaler: spec.main_upscaler,
    topazVersion: spec.topaz_version ?? '',
    maxBrightnessAdjustment: spec.max_brightness_adjustment ?? false,
    // Absent target width → native mode; source overlay → the 'Source' choice.
    targetMode: spec.target_size == null ? 'widest' : 'width',
    investmentUpscaler: spec.investment_use_source
      ? 'Source'
      : (spec.investment_upscaler ?? 'none'),
    mergeFeatherPx:
      spec.merge_feather_px != null ? String(spec.merge_feather_px) : '',
    // Absent → '' so a previously-picked preset's interpolation doesn't linger.
    mainSimpleInterpolation: spec.main_simple_interpolation ?? '',
    mainDownscaleInterpolation: spec.main_downscale_interpolation ?? '',
    mainResizeInterpolation: spec.main_resize_interpolation ?? '',
    investmentSimpleInterpolation: spec.investment_simple_interpolation ?? '',
    investmentDownscaleInterpolation:
      spec.investment_downscale_interpolation ?? '',
    investmentResizeInterpolation: spec.investment_resize_interpolation ?? '',
  }
  // Only carry a target width when the preset has one (native presets keep the
  // form's current width so switching back to explicit mode has a sane value).
  if (spec.target_size != null) patch.targetSize = spec.target_size
  if (spec.topaz_upscale_factor != null) {
    patch.topazUpscaleFactor = spec.topaz_upscale_factor as TopazUpscaleFactor
  }
  if (spec.topaz_power != null) patch.topazPower = spec.topaz_power
  if (spec.investment_upscale_factor != null) {
    patch.investmentUpscaleFactor =
      spec.investment_upscale_factor as TopazUpscaleFactor
  }
  if (spec.investment_power != null) {
    patch.investmentPower = spec.investment_power
  }
  return patch
}
